import { usePipelineStages } from '@/hooks/useMetrics';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ArrowRight, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

// Define detailed stage information
const STAGE_INFO = {
  Queued: {
    system: 'SQS Queue (entity-ingest)',
    condition: 'Webhook received via API Gateway',
    description: 'Documents waiting to be fetched from Snapshot API and transformed',
    layman: 'Just received the request to index this entity',
  },
  Compiled: {
    system: 'DynamoDB (entity-search-documents)',
    condition: 'Fetched from Snapshot API and transformed with meta-schema',
    description:
      'Documents processed and stored, waiting to be batched for AI processing',
    layman: 'Entity data fetched and prepared, waiting for AI summary',
  },
  Batched: {
    system: 'Anthropic Batch API',
    condition: 'Grouped into batch of 50 documents (or 10-minute timer)',
    description: 'Documents sent to Anthropic for AI summary generation (50% cost savings)',
    layman: 'Sent to AI service for profile summary generation',
  },
  'AI Processing': {
    system: 'Anthropic Batch API',
    condition: 'Batch job is in_progress status',
    description:
      'Anthropic is generating AI summaries (typically completes in 10-30 minutes)',
    layman: 'AI is writing the profile summary right now',
  },
  Ready: {
    system: 'SQS Queue (opensearch-index)',
    condition: 'AI summary completed and attached to document',
    description: 'Documents with AI summaries queued for OpenSearch indexing',
    layman: 'AI summary complete, ready to be indexed for search',
  },
  Indexed: {
    system: 'OpenSearch (via index aliases)',
    condition: 'Successfully indexed with 768D vector embeddings',
    description:
      'Documents fully indexed and searchable with semantic vector search (auto-deleted after 7 days)',
    layman: 'Fully indexed and searchable! This is the final state.',
  },
};

export default function PipelineVisualization() {
  const stages = usePipelineStages();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-600 border-green-500 hover:bg-green-500';
      case 'warning':
        return 'bg-yellow-600 border-yellow-500 hover:bg-yellow-500';
      case 'error':
        return 'bg-red-600 border-red-500 hover:bg-red-500';
      default:
        return 'bg-gray-600 border-gray-500 hover:bg-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-3 w-3" />;
      case 'warning':
        return <AlertTriangle className="h-3 w-3" />;
      case 'error':
        return <XCircle className="h-3 w-3" />;
      default:
        return null;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pipeline Flow</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <TooltipProvider delayDuration={200}>
          <div className="flex items-center gap-3 overflow-x-auto pb-4">
            {stages.map((stage, index) => (
              <div key={stage.name} className="flex items-center gap-3">
                {/* Stage Card with Tooltip */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className={cn(
                        'border-2 rounded-lg p-4 min-w-[140px] transition-all cursor-pointer',
                        getStatusColor(stage.status)
                      )}
                    >
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-white/90">
                            {stage.name}
                          </p>
                          <div className="text-white/90">{getStatusIcon(stage.status)}</div>
                        </div>
                        <p className="text-2xl font-bold text-white">
                          {stage.count.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="tooltip-content max-w-xs p-4 space-y-2">
                    <div>
                      <p className="font-semibold text-sm mb-1">{stage.name}</p>
                      <p className="text-xs text-muted-foreground italic">
                        {STAGE_INFO[stage.name as keyof typeof STAGE_INFO]?.layman}
                      </p>
                    </div>

                    {/* Warning explanation */}
                    {stage.status === 'warning' && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-2">
                        <p className="text-xs font-medium text-yellow-800 dark:text-yellow-200 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Why is this yellow?
                        </p>
                        <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                          {stage.name === 'Queued' && `Queue is backed up with ${stage.count.toLocaleString()} documents waiting. Normal processing should keep this under 1,000.`}
                          {stage.name === 'Compiled' && `${stage.count.toLocaleString()} documents waiting for AI batching. Normal batching should keep this under 500.`}
                          {stage.name === 'Ready' && `${stage.count.toLocaleString()} documents ready but not yet indexed. Indexer may be slow or backed up (threshold: 200).`}
                        </p>
                      </div>
                    )}

                    <div className="space-y-1 pt-2 border-t">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">System:</p>
                        <p className="text-xs">
                          {STAGE_INFO[stage.name as keyof typeof STAGE_INFO]?.system}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Condition:</p>
                        <p className="text-xs">
                          {STAGE_INFO[stage.name as keyof typeof STAGE_INFO]?.condition}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">
                          Technical:
                        </p>
                        <p className="text-xs">
                          {STAGE_INFO[stage.name as keyof typeof STAGE_INFO]?.description}
                        </p>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>

                {/* Arrow */}
                {index < stages.length - 1 && (
                  <ArrowRight className="h-6 w-6 text-muted-foreground flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </TooltipProvider>

        {/* Legend */}
        <div className="flex items-center gap-6 pt-4 border-t">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span className="text-sm text-muted-foreground">Healthy</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
            <span className="text-sm text-muted-foreground">Warning</span>
          </div>
          <div className="flex items-center gap-2">
            <XCircle className="h-4 w-4 text-red-500" />
            <span className="text-sm text-muted-foreground">Error</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
